import { useEffect, useMemo, useState } from 'react';
import availabilityService from '../services/availabilityService';

const getReadableError = (error, fallback = 'Something went wrong. Please try again.') => {
  if (!error) return fallback;

  if (typeof error === 'string') {
    return error;
  }

  if (typeof error?.message === 'string' && error.message.trim()) {
    return error.message;
  }

  if (typeof error?.error === 'string' && error.error.trim()) {
    return error.error;
  }

  return fallback;
};

const useBookingAvailability = ({
  currentStep,
  availabilityStepNumber = 2,
  eventDate,
  eventTime,
  searchRole
}) => {
  const [availabilityState, setAvailabilityState] = useState({
    loading: false,
    slots: [],
    error: '',
    searched: false
  });
  const [selectedSlotId, setSelectedSlotId] = useState(null);

  const availabilityWindow = useMemo(() => {
    if (!eventDate || !eventTime) {
      return null;
    }

    const start = new Date(`${eventDate}T${eventTime}:00`);
    if (Number.isNaN(start.getTime())) {
      return null;
    }

    const end = new Date(start.getTime() + 2 * 60 * 60 * 1000);
    return {
      from: start.toISOString(),
      to: end.toISOString()
    };
  }, [eventDate, eventTime]);

  const selectedSlot = useMemo(() => {
    if (!selectedSlotId) return null;
    return availabilityState.slots.find((slot) => Number(slot.id) === Number(selectedSlotId)) || null;
  }, [availabilityState.slots, selectedSlotId]);

  useEffect(() => {
    let cancelled = false;

    const loadAvailability = async () => {
      if (currentStep !== availabilityStepNumber || !availabilityWindow) {
        setAvailabilityState((prev) => ({
          ...prev,
          slots: [],
          error: '',
          searched: false,
          loading: false
        }));
        return;
      }

      setAvailabilityState({ loading: true, slots: [], error: '', searched: false });
      setSelectedSlotId(null);

      try {
        const slots = await availabilityService.searchSlots({
          actorRole: searchRole,
          from: availabilityWindow.from,
          to: availabilityWindow.to
        });

        if (cancelled) {
          return;
        }

        setAvailabilityState({
          loading: false,
          slots,
          error: '',
          searched: true
        });
      } catch (error) {
        if (cancelled) {
          return;
        }

        setAvailabilityState({
          loading: false,
          slots: [],
          error: getReadableError(error, 'Unable to fetch availability right now'),
          searched: true
        });
      }
    };

    loadAvailability();

    return () => {
      cancelled = true;
    };
  }, [currentStep, availabilityStepNumber, availabilityWindow, searchRole]);

  return {
    availabilityWindow,
    availabilityState,
    selectedSlot,
    selectedSlotId,
    setSelectedSlotId
  };
};

export default useBookingAvailability;
